import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

/**
 * QorCommerce Card-Token capture (web).
 *
 * Embeds the QorCommerce Card Token AppBloq in an iframe. The customer enters
 * their card directly into the QorCommerce-hosted form (card data never touches
 * our servers → low PCI scope). On success we receive a token via postMessage
 * and hand it back through onToken(token). The parent then calls placeOrder with
 * qorPaymentToken set, and the backend charges it against the restaurant's MID.
 *
 * Public env (safe for the browser — NOT the secret client key):
 *   REACT_APP_QOR_APP_ID     — app_id (the qor-app-key, ok to expose for the form)
 *   REACT_APP_QOR_CLIENT_ID  — client_id (the public form key)
 *   REACT_APP_QOR_FORMS_BASE — default https://secure.forms.qorcommerce.io
 */
const FORMS_BASE =
  process.env.REACT_APP_QOR_FORMS_BASE || "https://secure.forms.qorcommerce.io";

function buildVaultUrl({ appId, clientId, profileId, requestId }) {
  const params = new URLSearchParams({
    app_id: appId || "",
    client_id: clientId || "",
    intent: "create",
    cvv_field: "1",
    // The AppBloq's default label is "Create Card Token" — gateway jargon that
    // confuses customers. Saving is implicit (card vaults to the profile).
    button_text: "Save & Continue",
    textfield_type: "outlined",
  });
  if (profileId) params.set("profile_id", profileId);
  if (requestId) params.set("request_id", requestId);
  return `${FORMS_BASE}/vault/card/token?${params.toString()}`;
}

export default function QorPaymentForm({ profileId, onToken, onError }) {
  const [loaded, setLoaded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const requestIdRef = useRef(
    `rmf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );

  const appId = process.env.REACT_APP_QOR_APP_ID;
  const clientId = process.env.REACT_APP_QOR_CLIENT_ID;

  const handleMessage = useCallback(
    (e) => {
      // Only trust messages from the QorCommerce forms origin.
      if (typeof e.origin === "string" && !e.origin.includes("qorcommerce.io")) {
        return;
      }
      let data = e.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (_) {
          return;
        }
      }
      if (!data || typeof data !== "object") return;

      const event = data.event || data.status;
      if (event === "processing") {
        setProcessing(true);
        return;
      }
      if (event === "success_response" || data.token) {
        setProcessing(false);
        const token = data.token || (data.data && data.data.token);
        if (token && typeof onToken === "function") onToken(token, data);
        return;
      }
      if (event === "error_response") {
        setProcessing(false);
        const msg =
          data.message ||
          (data.data && data.data.message) ||
          "Card could not be saved. Please check your details and try again.";
        if (typeof onError === "function") onError(msg, data);
      }
    },
    [onToken, onError]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  if (!appId || !clientId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error" variant="body2">
          Card payment is not configured. Please choose another payment method.
        </Typography>
      </Box>
    );
  }

  const src = buildVaultUrl({
    appId,
    clientId,
    profileId,
    requestId: requestIdRef.current,
  });

  return (
    // Tall enough for the hosted form's full field list — avoids the cramped
    // inner scrollbar the shorter frame produced.
    <Box sx={{ position: "relative", minHeight: 520 }}>
      {!loaded && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}
      <iframe
        title="QorCommerce Card"
        src={src}
        onLoad={() => setLoaded(true)}
        style={{
          width: "100%",
          minHeight: 520,
          border: "none",
          opacity: loaded ? 1 : 0,
        }}
      />
      {processing && (
        <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
          Securing your card…
        </Typography>
      )}
    </Box>
  );
}
